'use client';

import { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { getBrowserClient } from './client';
import type { Profile, UserRole } from './types';
import * as Sentry from "@sentry/nextjs";

// ============================================================================
// Session Storage Keys
// ============================================================================

const PROFILE_CACHE_KEY = 'evrls_profile_cache';
const PROFILE_CACHE_TTL = 7 * 24 * 60 * 60 * 1000; // 1 week
const ADMIN_SELECTED_BUSINESS_KEY = 'admin_selected_business';

interface CachedProfile {
  profile: Profile;
  timestamp: number;
}

// ============================================================================
// Auth Context Types
// ============================================================================

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  role: UserRole | null;
  businessId: string | null;
  // Admin-specific fields
  isAdmin: boolean;
  adminSelectedBusinessId: string | null;
  effectiveBusinessId: string | null; // For admin: adminSelectedBusinessId, for others: businessId
  setAdminBusinessContext: (businessId: string | null) => Promise<void>;
  clearAdminBusinessContext: () => void;
  refreshProfile: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// Cache Helpers
// ============================================================================

function getCachedProfile(userId: string): Profile | null {
  if (typeof window === 'undefined') return null;
  
  try {
    const cached = localStorage.getItem(`${PROFILE_CACHE_KEY}_${userId}`);
    if (!cached) return null;
    
    const { profile, timestamp }: CachedProfile = JSON.parse(cached);
    
    // Check if cache is still valid
    if (Date.now() - timestamp > PROFILE_CACHE_TTL) {
      localStorage.removeItem(`${PROFILE_CACHE_KEY}_${userId}`);
      return null;
    }
    
    return profile;
  } catch {
    return null;
  }
}

function setCachedProfile(userId: string, profile: Profile): void {
  if (typeof window === 'undefined') return;
  
  try {
    const cached: CachedProfile = { profile, timestamp: Date.now() };
    localStorage.setItem(`${PROFILE_CACHE_KEY}_${userId}`, JSON.stringify(cached));
  } catch {
    // Ignore storage errors
  }
}

function clearCachedProfile(userId?: string): void {
  if (typeof window === 'undefined') return;
  
  try {
    if (userId) {
      localStorage.removeItem(`${PROFILE_CACHE_KEY}_${userId}`);
    } else {
      // Clear all profile caches
      Object.keys(localStorage)
        .filter(key => key.startsWith(PROFILE_CACHE_KEY))
        .forEach(key => localStorage.removeItem(key));
    }
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// Auth Provider Component
// ============================================================================

interface AuthProviderProps {
  children: React.ReactNode;
}

// Helper to get admin selected business from sessionStorage
function getAdminSelectedBusiness(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return sessionStorage.getItem(ADMIN_SELECTED_BUSINESS_KEY);
  } catch {
    return null;
  }
}

// Synchronous initializer for SSR-safe initialization
function getInitialAdminSelectedBusiness(): string | null {
  // Only call this on client-side during initial render
  if (typeof window === 'undefined') return null;
  return getAdminSelectedBusiness();
}

// Helper to set admin selected business in sessionStorage
function setAdminSelectedBusiness(businessId: string | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (businessId) {
      sessionStorage.setItem(ADMIN_SELECTED_BUSINESS_KEY, businessId);
    } else {
      sessionStorage.removeItem(ADMIN_SELECTED_BUSINESS_KEY);
    }
  } catch {
    // Ignore storage errors
  }
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  // Initialize admin selected business synchronously from sessionStorage to prevent flash of "no business" screen
  const [adminSelectedBusinessId, setAdminSelectedBusinessIdState] = useState<string | null>(() => getInitialAdminSelectedBusiness());
  const prefetchTriggeredRef = useRef(false);

  // Fetch user profile from the profiles table
  const fetchProfile = useCallback(async (userId: string, useCache = true): Promise<Profile | null> => {
    try {
      // Try to get cached profile first for instant load
      if (useCache) {
        const cachedProfile = getCachedProfile(userId);
        if (cachedProfile) {
          setProfile(cachedProfile);
          // Background refresh: verify cache is still valid but don't block
          // This runs in background and updates state if profile changed
          setTimeout(async () => {
            try {
              const supabase = getBrowserClient();
              const { data } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single();
              if (data && JSON.stringify(data) !== JSON.stringify(cachedProfile)) {
                setProfile(data as Profile);
                setCachedProfile(userId, data as Profile);
              }
            } catch {
              // Silently fail - cached data is still valid
            }
          }, 100);
          return cachedProfile;
        }
      }

      // Fetch from server with timeout
      const startTime = performance.now();
      const supabase = getBrowserClient();

      // Create a timeout promise - reduced to 3s for faster feedback
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error('Profile fetch timeout after 3s')), 3000);
      });

      // Race between the actual query and timeout
      const result = await Promise.race([
        supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .single(),
        timeoutPromise
      ]);

      const { data, error } = result;
      const endTime = performance.now();
      const fetchTime = Math.round(endTime - startTime);

      if (error) {
        // Profile might not exist yet (new user)
        if (error.code === 'PGRST116') {
          setProfile(null);
          return null;
        }
        Sentry.captureException(error);
        setProfile(null);
        return null;
      }

      const profileData = data as Profile;
      Sentry.logger.debug(`Profile loaded in ${fetchTime}ms`, {
        userId,
        email: profileData.email,
        role: profileData.role
      });
      setProfile(profileData);
      setCachedProfile(userId, profileData);
      
      // Trigger prefetching of business data after profile is loaded
      // Defer to next tick to not block profile loading
      if (profileData.business_id && !prefetchTriggeredRef.current) {
        prefetchTriggeredRef.current = true;
        // Defer prefetch to next tick so it doesn't block auth completion
        setTimeout(() => {
          // Dynamic import to avoid circular dependencies
          import('@/lib/features/swr/prefetch').then(({ prefetchBusinessData }) => {
            prefetchBusinessData(profileData.business_id!);
          }).catch(err => {
            Sentry.captureException(err);
          });
        }, 0);
      }
      
      return profileData;
    } catch (err) {
      Sentry.captureException(err);
      setProfile(null);
      return null;
    }
  }, []);

  // Refresh profile (can be called after profile updates)
  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchProfile(user.id);
    }
  }, [user?.id, fetchProfile]);

  // Admin business context management
  const setAdminBusinessContext = useCallback(async (businessId: string | null) => {
    if (profile?.role !== 'admin') {
      return;
    }

    setAdminSelectedBusinessIdState(businessId);
    setAdminSelectedBusiness(businessId);

    // Log the context switch to audit log via API
    if (businessId) {
      try {
        await fetch('/api/admin/switch-context', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ businessId }),
        });
      } catch (err) {
        // Don't block the context switch if audit log fails
        Sentry.captureException(err);
      }
    }
  }, [profile?.role]);

  const clearAdminBusinessContext = useCallback(() => {
    setAdminSelectedBusinessIdState(null);
    setAdminSelectedBusiness(null);
  }, []);

  // Sign out handler
  const handleSignOut = useCallback(async () => {
    const supabase = getBrowserClient();
    const userId = user?.id;
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setProfile(null);
    // Clear cached profile on sign out
    clearCachedProfile(userId);
    // Clear admin context on sign out
    clearAdminBusinessContext();
    prefetchTriggeredRef.current = false;
  }, [user?.id, clearAdminBusinessContext]);

  // Initialize auth state
  useEffect(() => {
    const supabase = getBrowserClient();
    let isMounted = true;
    let initializationComplete = false;
    let timeoutId: NodeJS.Timeout | null = null;

    // Listen for auth changes - set this up FIRST before getting session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        if (!isMounted) return;

        Sentry.logger.debug('Auth state changed', { event });

        setSession(newSession);
        setUser(newSession?.user ?? null);

        // Handle initial session or sign in
        if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && newSession?.user) {
          // Clear timeout since we're handling auth now
          if (timeoutId) {
            clearTimeout(timeoutId);
            timeoutId = null;
          }
          // Small delay on SIGNED_IN to allow Supabase to fully initialize
          // INITIAL_SESSION usually fires after the client is ready
          if (event === 'SIGNED_IN') {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
          await fetchProfile(newSession.user.id);
          // Mark loading as done after profile is fetched
          initializationComplete = true;
          setIsLoading(false);
        } else if (event === 'SIGNED_OUT' || (event === 'INITIAL_SESSION' && !newSession)) {
          // No session on initial load or signed out
          setProfile(null);
          clearCachedProfile();
          prefetchTriggeredRef.current = false;
          initializationComplete = true;
          setIsLoading(false);

          // Redirect to login if signed out (not initial load)
          if (event === 'SIGNED_OUT' && typeof window !== 'undefined') {
            // Only redirect if we're not already on an auth route
            const currentPath = window.location.pathname;
            const authRoutes = ['/', '/login', '/register', '/reset', '/reset-password', '/confirm-booking'];
            const isQueueRoute = currentPath.startsWith('/q');

            if (!authRoutes.includes(currentPath) && !isQueueRoute) {
              window.location.href = '/login';
            }
          }
        } else if (event === 'PASSWORD_RECOVERY' && newSession?.user) {
          // Handle password recovery - user clicked reset link from email
          // Don't redirect, just set session for password update
          setSession(newSession);
          setUser(newSession.user);
          initializationComplete = true;
          setIsLoading(false);
        } else if (event === 'USER_UPDATED' && newSession?.user) {
          await fetchProfile(newSession.user.id);
        } else if (event === 'TOKEN_REFRESHED') {
          // No need to refetch profile on token refresh
        }
      }
    );
    
    // Get initial session with timeout fallback
    const initializeAuth = async () => {
      // Set a timeout fallback in case getSession hangs
      // Reduced to 1.5s for faster fallback - cached profile provides data anyway
      timeoutId = setTimeout(() => {
        if (!initializationComplete && isMounted) {
          Sentry.logger.warn('Auth initialization timeout - falling back');
          setIsLoading(false);
        }
      }, 1500);
      
      try {
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();
        
        // Clear timeout since we got a response
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        
        if (!isMounted) return;
        
        if (error) {
          Sentry.captureException(error);
          setIsLoading(false);
          return;
        }
        
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        
        if (initialSession?.user) {
          await fetchProfile(initialSession.user.id);
        }
        
        initializationComplete = true;
        setIsLoading(false);
      } catch (err) {
        if (timeoutId) {
          clearTimeout(timeoutId);
          timeoutId = null;
        }
        Sentry.captureException(err);
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    initializeAuth();
    
    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  // Subscribe to profile changes via realtime to detect role updates
  useEffect(() => {
    if (!user?.id) return;

    const supabase = getBrowserClient();
    Sentry.logger.debug('Subscribing to profile changes', { userId: user.id });

    const channel = supabase
      .channel(`profile-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          Sentry.logger.debug('Profile updated via realtime', { 
            userId: user.id, 
            eventType: payload.eventType 
          });
          const updatedProfile = payload.new as Profile;
          
          // Clear cached profile to force refetch
          clearCachedProfile(user.id);
          
          // Refetch profile with fresh data (bypass cache)
          fetchProfile(user.id, false);
        }
      )
      .subscribe((status) => {
        Sentry.logger.debug('Profile subscription status changed', { 
          userId: user.id, 
          status 
        });
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, fetchProfile]);

  // Computed values
  const isAdmin = profile?.role === 'admin';
  const effectiveBusinessId = isAdmin ? adminSelectedBusinessId : (profile?.business_id ?? null);

  // PHASE 6: Memoize context value to prevent unnecessary re-renders
  // Only update when actual auth state changes
  const value: AuthContextType = useMemo(() => ({
    user,
    session,
    profile,
    isLoading,
    isAuthenticated: !!session && !!user,
    role: profile?.role ?? null,
    businessId: profile?.business_id ?? null,
    // Admin-specific
    isAdmin,
    adminSelectedBusinessId,
    effectiveBusinessId,
    setAdminBusinessContext,
    clearAdminBusinessContext,
    refreshProfile,
    signOut: handleSignOut,
  }), [
    user,
    session,
    profile,
    isLoading,
    isAdmin,
    adminSelectedBusinessId,
    effectiveBusinessId,
    setAdminBusinessContext,
    clearAdminBusinessContext,
    refreshProfile,
    handleSignOut,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// ============================================================================
// Auth Hook
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  
  return context;
}

// ============================================================================
// Role Check Helpers
// ============================================================================

/**
 * Check if the user has one of the allowed roles.
 */
export function hasRole(role: UserRole | null, allowedRoles: UserRole[]): boolean {
  if (!role) return false;
  return allowedRoles.includes(role);
}

/**
 * Check if user is an owner.
 */
export function isOwner(role: UserRole | null): boolean {
  return role === 'owner';
}

/**
 * Check if user is an owner or manager.
 */
export function isOwnerOrManager(role: UserRole | null): boolean {
  return hasRole(role, ['owner', 'manager']);
}

/**
 * Check if user is an admin.
 */
export function isAdminRole(role: UserRole | null): boolean {
  return role === 'admin';
}

// Re-export canAccessRoute from the centralized auth module
// This ensures consistent route access logic across the application
export { canAccessRoute } from '@/lib/core/auth';
